/**
 * Application configuration based on environment
 * Handles development vs production settings
 * Supports runtime configuration via /config/runtime-config.json
 */

export interface AppConfig {
  environment: 'dev' | 'qa' | 'prod' | 'local';
  isDevelopment: boolean;
  isProduction: boolean;
  isQa: boolean;
}

export interface RuntimeConfig {
  version?: string;
  apiUrls: {
    auth: string;
    office: string;
    kiosk: string;
    ocpiCpo?: string;
    ocpiAdmin?: string;
    s3BaseUrl?: string;
  };
  portalUrl: string;
  environment: 'dev' | 'qa' | 'prod';
  features?: {
    debugMode?: boolean;
    showTestCredentials?: boolean;
  };
}

class ConfigManager {
  private static instance: ConfigManager;
  private config: AppConfig;
  private runtimeConfig: RuntimeConfig | null = null;
  private runtimeConfigLoaded: boolean = false;

  private constructor() {
    // Get environment from VITE_ENV (dev, qa, prod, or local)
    const environment = (import.meta.env.VITE_ENV || 'dev') as 'dev' | 'qa' | 'prod' | 'local';

    this.config = {
      environment,
      isDevelopment: environment === 'dev',
      isProduction: environment === 'prod',
      isQa: environment === 'qa',
    };

  }

  static getInstance(): ConfigManager {
    if (!ConfigManager.instance) {
      ConfigManager.instance = new ConfigManager();
    }
    return ConfigManager.instance;
  }

  /**
   * Load runtime configuration from /config/runtime-config.json
   * This allows dynamic configuration without rebuilding the app
   * 
   * NOTE: Only used in production builds. During development (yarn dev/qa/prod),
   * we use VITE_ENV build-time configuration instead.
   */
  async loadRuntimeConfig(): Promise<void> {
    if (this.runtimeConfigLoaded) {
      return; // Already loaded
    }

    try {
      // Skip runtime config in development mode - use VITE_ENV instead
      // This prevents /public/config/runtime-config.json from overriding VITE_ENV
      const isDevelopmentServer = import.meta.env.DEV;
      if (isDevelopmentServer) {
        console.log('⚙️ Skipping runtime config load (dev server mode, using VITE_ENV)');
        this.runtimeConfigLoaded = true;
        return;
      }

      console.log('⚙️ Loading runtime config from /config/runtime-config.json...');
      const response = await fetch('/config/runtime-config.json', {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load runtime config: ${response.status}`);
      }

      this.runtimeConfig = await response.json();
      this.runtimeConfigLoaded = true;

      console.log('✅ Runtime config loaded successfully:', {
        environment: this.runtimeConfig.environment,
        authUrl: this.runtimeConfig.apiUrls.auth,
        officeUrl: this.runtimeConfig.apiUrls.office,
        kioskUrl: this.runtimeConfig.apiUrls.kiosk,
      });

      // Note: Environment is set from VITE_ENV and should not be overridden by runtime config
      // Runtime config is for dynamic settings like API URLs, not environment selection
    } catch (error) {
      // Failed to load runtime config, using build-time defaults
      console.error('❌ Failed to load runtime config, using build-time defaults:', error);
      console.warn('⚠️ Application will use VITE_ENV-based configuration');
      this.runtimeConfigLoaded = true; // Mark as loaded to prevent retries
    }
  }

  getRuntimeConfig(): RuntimeConfig | null {
    return this.runtimeConfig;
  }

  isRuntimeConfigLoaded(): boolean {
    return this.runtimeConfigLoaded;
  }

  getConfig(): AppConfig {
    return { ...this.config };
  }

  getEnvironment(): 'dev' | 'qa' | 'prod' {
    return this.config.environment;
  }

  isDevelopment(): boolean {
    return this.config.isDevelopment;
  }

  isProduction(): boolean {
    return this.config.isProduction;
  }

  isQa(): boolean {
    return this.config.isQa;
  }

  /**
   * Get application version with fallback logic
   * Returns runtime version if available, otherwise falls back to git version
   * @returns Object with version string and isFallback flag
   */
  getVersion(): { version: string; isFallback: boolean } {
    // Try to get version from runtime config first
    if (this.runtimeConfig?.version) {
      return {
        version: this.runtimeConfig.version,
        isFallback: false
      };
    }
    
    // Fall back to git version
    return {
      version: __GIT_COMMIT_HASH_SHORT__,
      isFallback: true
    };
  }

  /**
   * Logs the current configuration to console
   * Useful for debugging and verifying settings
   */
  logConfig(): void {
    const config = this.getConfig();
    console.group('🔧 Application Configuration');
    console.log('Environment:', config.environment);
    console.log('Runtime Config Loaded:', this.runtimeConfigLoaded);
    if (this.runtimeConfig) {
      console.log('Runtime Config:', this.runtimeConfig);
    }
    console.groupEnd();
  }
}

// Export singleton instance
export const appConfig = ConfigManager.getInstance();